import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { IsoDate, Range } from '@softline/core';
import { ButtonComponent, InputComponent, NumberInputComponent, TranslatePipe } from '@softline/ui-core';
import { DateInputComponent } from '@softline/ui-core/date-input';
import { FilterItemConfig } from '../../../../../types/filter-config';
import { FilterOperator } from '../../../../../types/filter-operators';
import { isValuelessOperator } from '../../../../../helper/builder-operators.function';

type ValueInputMode = 'none' | 'multi' | 'text' | 'number' | 'number-range' | 'date' | 'date-range';

@Component({
  selector: 'soft-filter-value-input',
  imports: [
    ButtonComponent,
    InputComponent,
    NumberInputComponent,
    DateInputComponent,
    TranslatePipe
  ],
  templateUrl: './filter-value-input.component.html',
  styleUrl: './filter-value-input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterValueInputComponent {
  readonly itemConfig = input.required<FilterItemConfig<any>>();
  readonly operator = input.required<FilterOperator>();
  readonly value = input<unknown>(null);

  readonly valueChange = output<unknown>();
  readonly editValues = output<void>();

  readonly mode = computed<ValueInputMode>(() => {
    const operator = this.operator();
    if (isValuelessOperator(operator)) return 'none';
    if (operator === 'in' || operator === 'notIn') return 'multi';
    const type = this.itemConfig().type;
    switch (type) {
      case 'string':
        return 'text';
      case 'number':
        return operator === 'between' ? 'number-range' : 'number';
      case 'date':
      case 'dateTime':
      case 'dateTimeTz':
        return operator === 'between' ? 'date-range' : 'date';
      default:
        return 'none';
    }
  });

  readonly selectionCount = computed(() => {
    const value = this.value();
    return Array.isArray(value) ? value.length : 0;
  });

  readonly textValue = computed(() => (typeof this.value() === 'string' ? (this.value() as string) : null));
  readonly numberValue = computed(() => (typeof this.value() === 'number' ? (this.value() as number) : null));
  readonly dateValue = computed(() => (typeof this.value() === 'string' ? (this.value() as IsoDate) : null));

  readonly rangeValue = computed<Range<any>>(() => {
    const value = this.value();
    if (value && typeof value === 'object' && 'from' in value && 'to' in value)
      return value as Range<any>;
    return { from: null, to: null } as unknown as Range<any>;
  });

  onRangeChange(part: 'from' | 'to', partValue: unknown): void {
    this.valueChange.emit({ ...this.rangeValue(), [part]: partValue });
  }
}
